import React, { useState } from "react";
import { api, arabicError } from "../lib/api";
import { toast } from "sonner";
import { X, MapPin, Truck, Store, ExternalLink } from "lucide-react";
import { useNavigate } from "react-router-dom";
import LocationPicker from "./LocationPicker";
import { unitDisplay, badCountQty, INT_QTY_MSG, qtyInputProps } from "../lib/constants";

const inputCls = "h-12 w-full rounded-lg border border-[#7AB2B2] bg-white px-4 focus:outline-none focus:ring-2 focus:ring-[#09637E]";
// Phase 5C-2C: labels only; missing unit (historical products) → meter wording
const QTY_LABEL = { kg: "كم كيلوغرام مطلوب؟", liter: "كم لتر مطلوب؟", piece: "كم قطعة مطلوبة؟", shoe_size: "كم قطعة مطلوبة؟" };
const QTY_REQUIRED = { kg: "أدخل عدد الكيلوغرامات", liter: "أدخل عدد اللترات", piece: "أدخل عدد القطع", shoe_size: "أدخل عدد القطع" };

const ProductSummary = ({ product, priceUsd, available }) => (
  <div className="p-4 bg-[#EBF4F6] flex gap-4">
    {product.image_url && <img src={product.image_url} alt="" className="w-24 h-24 rounded-lg object-cover" />}
    <div className="flex-1 min-w-0">
      <div className="font-bold text-[#333333] truncate">{product.merchant_name}</div>
      <div className="text-sm text-[#666666]">{product.size} · {product.color}</div>
      <div className="text-sm text-[#666666]">{product.category} · {product.country}</div>
      <div className="text-lg font-extrabold text-[#09637E] mt-1" dir="ltr" data-testid="reservation-unit-price">${priceUsd.toFixed(2)} / {unitDisplay(product.unit).short}</div>
      <div className="text-xs text-[#666666]" data-testid="reservation-available">المتوفر: {available} {unitDisplay(product.unit).short}</div>
    </div>
  </div>
);

const MethodSelector = ({ method, setMethod }) => (
  <div>
    <label className="block text-sm font-bold text-[#333333] mb-2">طريقة التسليم *</label>
    <div className="grid grid-cols-2 gap-2">
      <button type="button" onClick={() => setMethod("pickup")} data-testid="method-pickup"
        className={`h-14 rounded-lg border-2 font-bold flex items-center justify-center gap-2 transition-colors ${method === "pickup" ? "bg-[#09637E] text-white border-[#09637E]" : "bg-white text-[#09637E] border-[#7AB2B2]"}`}>
        <Store size={18} /> استلام من المركز
      </button>
      <button type="button" onClick={() => setMethod("delivery")} data-testid="method-delivery"
        className={`h-14 rounded-lg border-2 font-bold flex items-center justify-center gap-2 transition-colors ${method === "delivery" ? "bg-[#09637E] text-white border-[#09637E]" : "bg-white text-[#09637E] border-[#7AB2B2]"}`}>
        <Truck size={18} /> توصيل البضاعة
      </button>
    </div>
  </div>
);

const PickupInfo = ({ product }) => {
  const sellerFullAddress = [product.merchant_city, product.merchant_address].filter(Boolean).join(" — ");
  const loc = product.merchant_location;
  const mapsLink = loc ? `https://www.google.com/maps?q=${loc.lat},${loc.lng}` : null;
  return (
    <div className="rounded-lg border-2 border-[#7AB2B2] bg-[#EBF4F6]/50 p-4 space-y-2" data-testid="pickup-info">
      <div className="flex items-center gap-2 text-[#09637E] font-extrabold">
        <Store size={18} /> عنوان محل البائع
      </div>
      <div className="text-[#333333] text-sm leading-relaxed">
        {sellerFullAddress || <span className="text-[#666666]">لم يُدرج البائع عنواناً تفصيلياً</span>}
      </div>
      {mapsLink ? (
        <a href={mapsLink} target="_blank" rel="noopener noreferrer"
          data-testid="seller-maps-link"
          className="inline-flex items-center gap-1 mt-1 h-10 px-4 rounded-lg bg-[#09637E] hover:bg-[#088395] text-white font-bold text-sm">
          <MapPin size={16} /> فتح الموقع على خرائط جوجل <ExternalLink size={14} />
        </a>
      ) : (
        <p className="text-xs text-[#666666]">لم يُحدد البائع موقعاً على الخريطة</p>
      )}
    </div>
  );
};

const DeliveryFields = ({ address, setAddress, phone, setPhone, deliveryLocation, setDeliveryLocation }) => (
  <>
    <div className="rounded-lg border border-amber-300 bg-amber-50 p-3 text-sm text-amber-800 font-bold leading-relaxed" data-testid="delivery-price-note">
      ملاحظة: السعر المعروض لا يشمل رسوم التوصيل، يرجى مراسلة البائع مباشرة للاتفاق على سعر النقل.
    </div>
    <div>
      <label className="block text-sm font-bold text-[#333333] mb-1">العنوان بالتفصيل *</label>
      <textarea required value={address} onChange={(e) => setAddress(e.target.value)} rows={2}
        data-testid="reservation-address"
        className="w-full rounded-lg border border-[#7AB2B2] bg-white px-4 py-2 focus:outline-none focus:ring-2 focus:ring-[#09637E]"
        placeholder="مدينة، حي، شارع، معالم قريبة..." />
    </div>
    <div>
      <label className="block text-sm font-bold text-[#333333] mb-1">إضافة الموقع على الخريطة *</label>
      <LocationPicker value={deliveryLocation} onChange={setDeliveryLocation} testidPrefix="delivery-loc" />
      {deliveryLocation && (
        <p className="text-xs text-[#09637E] font-bold mt-1" dir="ltr">
          ✓ {deliveryLocation.lat.toFixed(5)}, {deliveryLocation.lng.toFixed(5)}
        </p>
      )}
    </div>
    <div>
      <label className="block text-sm font-bold text-[#333333] mb-1">رقم التواصل *</label>
      <input type="tel" required value={phone}
        onChange={(e) => setPhone(e.target.value)}
        data-testid="reservation-phone" className={inputCls} placeholder="09xxxxxxxx" />
    </div>
  </>
);

const ReservationModal = ({ product, onClose, onSuccess }) => {
  const navigate = useNavigate();
  const [meters, setMeters] = useState("");
  const [address, setAddress] = useState("");
  const [method, setMethod] = useState("pickup");
  const [phone, setPhone] = useState("");
  const [deliveryLocation, setDeliveryLocation] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  const available = parseFloat(product.quantity_available ?? product.quantity ?? 0);
  const priceUsd = parseFloat(product.price_usd ?? product.price ?? 0);
  const totalUsd = (parseFloat(meters || 0) * priceUsd).toFixed(2);
  const u = unitDisplay(product.unit).short;

  const validate = (m) => {
    if (!m || m <= 0) return QTY_REQUIRED[product.unit] || "أدخل عدد الأمتار";
    if (badCountQty(product.unit, m)) return INT_QTY_MSG;
    if (m > available) return `الكمية المتوفرة: ${available} ${u} فقط`;
    if (method === "delivery") {
      if (!address.trim()) return "العنوان بالتفصيل مطلوب";
      if (!phone.trim()) return "رقم التواصل مطلوب للتوصيل";
      if (!deliveryLocation) return "حدد موقعك على الخريطة";
    }
    return null;
  };

  const submit = async (e) => {
    e.preventDefault();
    const m = parseFloat(meters);
    const err = validate(m);
    if (err) { toast.error(err); return; }
    setSubmitting(true);
    try {
      const payload = {
        product_id: product.id,
        meters: m,
        address: method === "pickup" ? "استلام من المركز" : address.trim(),
        delivery_method: method,
        delivery_phone: phone.trim(),
      };
      if (method === "delivery") payload.delivery_location = deliveryLocation;
      const r = await api.post("/reservations", payload);
      toast.success("تم إرسال طلب الحجز");
      onSuccess?.(r.data);
      onClose();
      navigate(`/messages/${r.data.id}`);
    } catch (err2) { toast.error(arabicError(err2)); }
    finally { setSubmitting(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={onClose} dir="rtl">
      <div className="bg-white rounded-2xl max-w-lg w-full max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="reservation-modal">
        <div className="sticky top-0 bg-white border-b border-[#7AB2B2] p-4 flex items-center justify-between">
          <h3 className="text-xl font-extrabold text-[#333333]">طلب حجز</h3>
          <button onClick={onClose} data-testid="close-reservation" className="w-10 h-10 rounded-lg hover:bg-[#EBF4F6] flex items-center justify-center"><X /></button>
        </div>

        <ProductSummary product={product} priceUsd={priceUsd} available={available} />

        <form onSubmit={submit} className="p-4 space-y-4" data-testid="reservation-form">
          <div>
            <label className="block text-sm font-bold text-[#333333] mb-1" data-testid="reservation-qty-label">{QTY_LABEL[product.unit] || "كم متر مطلوب؟"} *</label>
            <input type="number" step="0.1" min="0.1" {...qtyInputProps(product.unit)} max={available} required value={meters}
              onChange={(e) => setMeters(e.target.value)}
              data-testid="reservation-meters" className={inputCls} placeholder={`حد أقصى ${available} ${u}`} />
            {meters && <p className="text-sm text-[#09637E] font-bold mt-1">الإجمالي: ${totalUsd}</p>}
          </div>

          <MethodSelector method={method} setMethod={setMethod} />

          {method === "pickup" && <PickupInfo product={product} />}
          {method === "delivery" && (
            <DeliveryFields
              address={address} setAddress={setAddress}
              phone={phone} setPhone={setPhone}
              deliveryLocation={deliveryLocation} setDeliveryLocation={setDeliveryLocation}
            />
          )}

          <button type="submit" disabled={submitting} data-testid="reservation-submit"
            className="h-12 w-full rounded-lg bg-[#09637E] hover:bg-[#088395] text-white font-extrabold flex items-center justify-center gap-2 disabled:opacity-60">
            <MapPin size={18} /> {submitting ? "جارٍ الإرسال..." : "إرسال الطلب"}
          </button>
        </form>
      </div>
    </div>
  );
};

export default ReservationModal;
